// frontend/src/components/AnnouncementBoard.js
import React, { useState, useEffect } from 'react';
import { Card, Carousel, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaBullhorn, FaImage, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';
import '../styles/announcementBoardDark.css';

const AnnouncementBoard = ({ className = '' }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 获取活跃公告
  const fetchActiveAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/announcements/active');
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('获取公告失败:', error);
      setError('获取公告失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveAnnouncements();
    
    // 每5分钟刷新一次公告
    const interval = setInterval(fetchActiveAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 获取优先级样式
  const getPriorityClass = (priority) => {
    if (priority >= 8) return 'priority-high';
    if (priority >= 5) return 'priority-medium';
    return 'priority-low';
  };

  // 获取优先级标签
  const getPriorityBadge = (priority) => {
    if (priority >= 8) return <Badge bg="danger" className="me-2 priority-badge"><FaExclamationTriangle className="me-1" />重要</Badge>;
    if (priority >= 5) return <Badge bg="warning" className="me-2 priority-badge">一般</Badge>;
    return <Badge bg="info" className="me-2 priority-badge">普通</Badge>;
  };

  if (loading && announcements.length === 0) {
    return (
      <Card className={`announcement-board ${className}`}>
        <Card.Header 
          className="announcement-header"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
          }}
        >
          <h5 className="mb-0" style={{ color: 'white' }}>
            <FaBullhorn className="header-icon" style={{ color: 'white' }} />
            公告栏 - 即将上架机器预告
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="text-primary" />
          <div className="mt-2 text-muted">正在加载公告...</div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`announcement-board ${className}`}>
        <Card.Header 
          className="announcement-header"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
          }}
        >
          <h5 className="mb-0" style={{ color: 'white' }}>
            <FaBullhorn className="header-icon" style={{ color: 'white' }} />
            公告栏 - 即将上架机器预告
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="mb-0 d-flex align-items-center">
            <FaExclamationTriangle className="me-2" />
            <small>{error}</small>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (announcements.length === 0) {
    return (
      <Card className={`announcement-board ${className}`}>
        <Card.Header 
          className="announcement-header"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
          }}
        >
          <h5 className="mb-0" style={{ color: 'white' }}>
            <FaBullhorn className="header-icon" style={{ color: 'white' }} />
            公告栏 - 即将上架机器预告
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <div className="text-muted">
            <span className="empty-icon">📭</span>
            <div className="mt-2">暂无公告信息</div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (      <Card className={`announcement-board ${className}`}>
        <Card.Header 
          className="announcement-header"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
          }}
        >
          <h5 className="mb-0 d-flex align-items-center justify-content-between" style={{ color: 'white' }}>
            <span>
              <FaBullhorn className="header-icon" style={{ color: 'white' }} />
              公告栏 - 即将上架机器预告
            </span>
            <Badge bg="light" text="dark" className="ms-2">
              {announcements.length} 条
            </Badge>
          </h5>
        </Card.Header>
      <Card.Body className="p-0">
        {announcements.length === 1 ? (
          // 单条公告直接显示
          <div className={`announcement-item ${getPriorityClass(announcements[0].priority)} ${announcements[0].imageUrl ? 'has-image' : ''}`}>
            {announcements[0].imageUrl && (
              <div className="announcement-background-image">
                <img 
                  src={`${api.defaults.baseURL}${announcements[0].imageUrl}`}
                  alt="公告背景图片"
                  loading="lazy"
                />
                <div className="image-overlay"></div>
              </div>
            )}
            <div className="announcement-content">
              <div className="announcement-title">
                {getPriorityBadge(announcements[0].priority)}
                {announcements[0].title}
              </div>
              <div className="announcement-text">
                {announcements[0].content}
              </div>
            </div>
          </div>
        ) : (
          // 多条公告使用轮播
          <Carousel 
            indicators={announcements.length > 1} 
            controls={announcements.length > 1}
            interval={10000}
            className={`announcement-carousel ${announcements.length <= 1 ? 'hide-controls' : ''}`}
            pause="hover"
          >
            {announcements.map((announcement, index) => (
              <Carousel.Item key={announcement._id}>
                <div className={`announcement-item ${getPriorityClass(announcement.priority)} ${announcement.imageUrl ? 'has-image' : ''}`}>
                  {announcement.imageUrl && (
                    <div className="announcement-background-image">
                      <img 
                        src={`${api.defaults.baseURL}${announcement.imageUrl}`}
                        alt="公告背景图片"
                        loading="lazy"
                      />
                      <div className="image-overlay"></div>
                    </div>
                  )}
                  <div className="announcement-content">
                    <div className="announcement-title">
                      {getPriorityBadge(announcement.priority)}
                      {announcement.title}
                    </div>
                    <div className="announcement-text">
                      {announcement.content}
                    </div>
                  </div>
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        )}
      </Card.Body>
    </Card>
  );
};

export default AnnouncementBoard;
